import React from "react";

export type AlertRole = "alert" | "status" | "note";

interface AlertProps {
  variant?: "warning" | "danger" | "success" | "info";
  className?: string;
  role?: AlertRole;
  "data-testid"?: string;
  children: React.ReactNode;
}

/**
 * Each variant carries a marker class alongside its utilities. The utilities
 * paint the legacy bordered box; the marker is what the design's stylesheets
 * key off to state the same alert as one flat panel (design-flow.panel-notices,
 * booking-form-mfe.scss), so a themed surface needs no extra class per call
 * site.
 */
const variantStyles: Record<string, string> = {
  warning: "gcw:bg-amber-50 gcw:border-warning gcw:text-amber-800 widget-warning-panel",
  danger: "gcw:bg-red-50 gcw:border-error gcw:text-red-800 widget-error-panel",
  success: "gcw:bg-green-50 gcw:border-success gcw:text-green-800 widget-success-panel",
  info: "gcw:bg-alert-info gcw:border-alert-info-border gcw:text-black",
};

export function Alert({
  variant = "info",
  className = "",
  role = "alert",
  "data-testid": dataTestId,
  children,
}: Readonly<AlertProps>) {
  return (
    <div
      role={role}
      className={`gcw:border gcw:rounded-md gcw:p-bs-3 gcw:text-body ${variantStyles[variant] ?? ""} ${className}`}
      data-testid={dataTestId}
    >
      {children}
    </div>
  );
}
